import React, { useState, useMemo, useEffect } from "react";
import { FaSearch, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoIosInformationCircle } from "react-icons/io";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import { apiFetch } from "../api_call";

export default function Approved() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const rowsPerPage = 8;

  const navigate = useNavigate();

  // ================= ADMIN AUTH =================
  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    if (!isLoggedIn || user.usrlst_role?.toLowerCase() !== "admin") {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // ================= FETCH DATA =================
 useEffect(() => {
  const fetchApproved = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch("/report/admin/compliance");

      const formatted = response.data
        .filter(item => item.status?.toLowerCase() === "approved")
        .map(item => ({
          id: item.report_id,
          act: item.act,
          particular: item.name,
          title: item.title || "",
          description: item.description || "",
          startDate: item.start_date,
          actionDate: item.action_date,
          endDate: item.end_date,
          status: item.status,
          bu: item.bu_name || "-",
          department: item.dept_name || "-",
          userName: item.user_name || "-",
          approversEmail: item.approvers_email || "-",
          requestDate: item.request_date || "",
          responseDate: item.response_date || "",
          evidenceDocument: item.evidence_document || null,
          evidenceType: item.evidence_document_type || null,
        }));

      setData(formatted);
    } catch (error) {
      console.error("Error fetching approved compliances:", error);
      setError("Failed to fetch approved records.");
    } finally {
      setLoading(false);
    }
  };

  fetchApproved();
}, []);

  // ================= MIME TYPE =================
  const getMimeType = (extension) => {
    const types = {
      ".pdf": "application/pdf",
      ".jpg": "image/jpeg",
      ".jpeg": "image/jpeg",
      ".png": "image/png",
      ".csv": "text/csv",
      ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ".xls": "application/vnd.ms-excel",
      ".doc": "application/msword",
      ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ".txt": "text/plain"
    };
    return types[extension?.toLowerCase()] || "application/octet-stream";
  };

  const downloadDocument = (base64Data, extension, id) => {
    if (!base64Data) return;

    const mimeType = getMimeType(extension);
    const link = document.createElement("a");
    link.href = `data:${mimeType};base64,${base64Data}`;
    link.download = `compliance_${id}${extension || ""}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ================= FILTER =================
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  // ================= SORT =================
  const exportToCSV = () => {
  const headers = [
    "Id",
    "Compliance",
    "Particular",
    "Title",
    "Description",
    "Start Date",
    "Action Date",
    "End Date",
    "Status",
    "User",
    "BU",
    "Department",
    "Approver Email",
    "Request Date",
    "Response Date",
    "Has Document"
  ];

  const rows = sortedData.map((r) => [
    r.id,
    r.act,
    r.particular,
    r.title,
    r.description,
    r.startDate,
    r.actionDate,
    r.endDate,
    r.status,
    r.userName,
    r.bu,
    r.department,
    r.approversEmail,
    r.requestDate,
    r.responseDate,
    r.evidenceDocument ? "Yes" : "No"
  ]);

  const csv =
    [headers, ...rows]
      .map((e) => e.map((v) => `"${v ?? ""}"`).join(","))
      .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "approved_report.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
  const sortedData = useMemo(() => {
    let sortable = [...filteredData];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const valA = (a[sortConfig.key] || "").toString().toLowerCase();
        const valB = (b[sortConfig.key] || "").toString().toLowerCase();
        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredData, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="sort-icon" />;
    return sortConfig.direction === "asc"
      ? <FaSortUp className="sort-icon" />
      : <FaSortDown className="sort-icon" />;
  };

  // ================= PAGINATION =================
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const statusCounts = useMemo(() => {
  const counts = {
    approved: 0
  };

  filteredData.forEach((item) => {
    counts.total++;

    const status = item.status?.toLowerCase();
    if (status === "approved") counts.approved++;
  });

  return counts;
}, [filteredData]);

  // ================= UI =================
  return (
    <div className="Approved">
      <Sidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="headSection">
        <div className="compliance-score">Approved Report</div>
      </div>

      <div className="table-header" style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: "10px", marginLeft: "45px" }}>
          <button className="btn blue-btn" onClick={() => navigate("/comprehensive")}>Comprehensive</button>
          <button className="btn blue-btn" onClick={() => navigate("/pending")}>Pending</button>
          <button className="btn gray-btn">Approved</button>
          <button className="btn blue-btn" onClick={() => navigate("/requested")}>Requested</button>
          <button className="btn blue-btn" onClick={() => navigate("/overdue")}>Overdue</button>
        </div>

        <div className="table-actions" style={{ display: "flex", gap: "10px" }}>
          <div style={{ display: "flex", gap: "10px" }}>
    
   

    <div style={{
      background: "#d4edda",
      color: "#155724",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Approved: {statusCounts.approved}
    </div>

  </div>

  <div className="search-box">
    <input
      placeholder="Search"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
    />
    <FaSearch className="search-icon" />
  </div>

  <button className="action-btn primary" onClick={exportToCSV}>
    Export
  </button>
</div>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th onClick={() => requestSort("id")}>Id {getSortIcon("id")}</th>
            <th onClick={() => requestSort("act")}>Compliance {getSortIcon("act")}</th>
            <th>Instance</th>
            <th onClick={() => requestSort("startDate")}>Start Date</th>
            <th onClick={() => requestSort("actionDate")}>Action Date</th>
            <th onClick={() => requestSort("endDate")}>End Date</th>
            <th>Status</th>
            <th>User</th>
            <th>Approver's Email</th>
            <th>Request Date</th>
            <th>Response Date</th>
            <th>Document</th>
          </tr>
        </thead>

       <tbody>
  {loading ? (
    <tr>
      <td colSpan="12" style={{ textAlign: "center", padding: "20px" }}>
        Loading approved records...
      </td>
    </tr>
  ) : error ? (
    <tr>
      <td colSpan="12" style={{ textAlign: "center", color: "red" }}>
        {error}
      </td>
    </tr>
  ) : currentRows.length > 0 ? (
    currentRows.map((row, index) => (
      <tr key={index}>
        <td>{row.id}</td>
        <td>{row.act}</td>
        <td>
  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
    <span>
      {row.title
        ? `${row.particular} - ${row.title}`
        : row.particular}
    </span>

    {row.description && (
      <IoIosInformationCircle
        title={row.description}
        style={{
          cursor: "pointer",
          fontSize: "16px",
          color: "#1A1744"
        }}
      />
    )}
  </div>
</td>
        <td>{row.startDate}</td>
        <td>{row.actionDate}</td>
        <td>{row.endDate}</td>
        <td style={{ color: "green", fontWeight: 600 }}>{row.status}</td>
        <td>
  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
    <span>{row.userName}</span>

    {(row.bu || row.department) && (
      <IoIosInformationCircle
        title={`BU: ${row.bu} | Dept: ${row.department}`}
        style={{
          cursor: "pointer",
          fontSize: "16px",
          color: "#1A1744"
        }}
      />
    )}
  </div>
</td>
        <td>{row.approversEmail}</td>
        <td>{row.requestDate}</td>
        <td>{row.responseDate}</td>
        <td>
          {row.evidenceDocument ? (
            <button
              className="btn blue-btn"
              style={{ padding: "4px 8px", fontSize: "12px" }}
              onClick={() =>
                downloadDocument(
                  row.evidenceDocument,
                  row.evidenceType,
                  row.id
                )
              }
            >
              Download
            </button>
          ) : "-"}
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="12" style={{ textAlign: "center" }}>
        No approved records found
      </td>
    </tr>
  )}
</tbody>
      </table>

      <div className="pagination">
        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
        <span>Page {currentPage} of {totalPages}</span>
        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
      </div>
    </div>
  );
}